import * as RadixTabs from '@radix-ui/react-tabs';
import classNames from 'classnames';
import { memo, type ReactNode } from 'react';

import { cn } from '@air/utils-tailwind';

export type TabPanel = {
  content: ReactNode;
  contentClassName?: string;
  disabled?: boolean;
  id: string;
  label: ReactNode | string;
  labelClassName?: string;
};

export type TabsProps = Pick<
  RadixTabs.TabsProps,
  'activationMode' | 'className' | 'defaultValue' | 'dir' | 'onValueChange' | 'orientation' | 'value'
> &
  Pick<RadixTabs.TabsListProps, 'loop'> & {
    defaultValue?: string;
    listClassName?: string;
    panels: TabPanel[];
    panelsClassName?: string;
    rightControls?: ReactNode;
  };

export const Tabs = memo(
  ({
    activationMode,
    className,
    defaultValue,
    dir,
    listClassName,
    loop,
    onValueChange,
    orientation,
    panels = [],
    panelsClassName,
    rightControls,
    ...rest
  }: TabsProps) => {
    const defaultTab = defaultValue || panels?.[0]?.id;

    return (
      <RadixTabs.Root
        activationMode={activationMode}
        className={className}
        defaultValue={defaultTab}
        dir={dir}
        onValueChange={onValueChange}
        orientation={orientation}
        {...rest}
      >
        <RadixTabs.List className={cn('flex justify-between border-b border-b-gray-5', listClassName)} loop={loop}>
          <div className="flex gap-5">
            {panels?.map((panel) => (
              <RadixTabs.Trigger
                className={classNames(
                  'group/tabs-trigger -mb-px cursor-pointer radix-disabled:cursor-not-allowed select-none border-t-2 border-t-transparent border-b-2 border-b-transparent radix-state-active:ads:border-b-primary radix-state-active:border-b-blue-9 font-semibold ads:text-primary radix-state-active:ads:text-primary radix-state-active:text-gray-12 text-12 text-gray-12 radix-disabled:opacity-45',
                  panel.labelClassName,
                )}
                data-tabid={panel.id}
                data-testid="TAB_TRIGGER"
                disabled={panel.disabled}
                key={panel.id}
                value={panel.id}
              >
                <div className="-mx-2 mb-1 flex items-center gap-1 rounded-4 bg-transparent px-2 py-1 group-hover/tabs-trigger:bg-gray-4 group-active/tabs-trigger:bg-gray-4">
                  {panel.label}
                </div>
              </RadixTabs.Trigger>
            ))}
          </div>
          {rightControls}
        </RadixTabs.List>

        {panels?.map((panel) => (
          <RadixTabs.Content
            className={classNames(panelsClassName, panel.contentClassName)}
            key={panel.id}
            value={panel.id}
          >
            {panel.content}
          </RadixTabs.Content>
        ))}
      </RadixTabs.Root>
    );
  },
);

Tabs.displayName = 'Tabs';
